// Rule-based AI demo answers (~20% path). These are COMPUTED from the live demo
// dataset so the assistant feels grounded — no external model calls.

function inr(n) {
  return "₹ " + Math.round(Number(n) || 0).toLocaleString("en-IN");
}

function topCategory(inventory) {
  const counts = {};
  for (const it of inventory) {
    const tab = String(it.item_tab || "other");
    counts[tab] = (counts[tab] || 0) + 1;
  }
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted[0] || ["—", 0];
}

const RULES = [
  {
    id: "top_category",
    match: ["top", "best selling", "top selling", "popular", "category", "categories"],
    run: ({ inventory }) => {
      const [cat, n] = topCategory(inventory);
      return `Your largest in-stock category is "${cat}" with ${n} pieces. Rings and necklaces dominate the floor; chains and coins turn over fastest by count.`;
    },
  },
  {
    id: "inventory_summary",
    match: ["inventory", "stock summary", "how much stock", "stock value", "items in stock"],
    run: ({ inventory }) => {
      const available = inventory.filter((i) => i.status === "available");
      const grams = available.reduce((s, i) => s + Number(i.net_weight || 0), 0);
      return `You have ${available.length} items available (${inventory.length} total), totalling about ${grams.toFixed(
        1
      )} g net. The heaviest pieces are antique bangles and the temple necklace.`;
    },
  },
  {
    id: "customer_summary",
    match: ["customer", "customers", "clients", "who owes", "receivable"],
    run: ({ customers }) => {
      const owing = customers.filter((c) => Number(c.pending_cash || 0) > 0);
      const totalDue = customers.reduce((s, c) => s + Number(c.pending_cash || 0), 0);
      const top = [...owing].sort((a, b) => b.pending_cash - a.pending_cash)[0];
      return `You have ${customers.length} customers; ${owing.length} carry an outstanding balance totalling ${inr(
        totalDue
      )}.${top ? ` The largest is ${top.name} at ${inr(top.pending_cash)}.` : ""}`;
    },
  },
  {
    id: "repair_summary",
    match: ["repair", "repairs", "workshop", "job status"],
    run: ({ repairs }) => {
      const by = {};
      for (const r of repairs) by[r.status] = (by[r.status] || 0) + 1;
      const parts = Object.entries(by).map(([s, n]) => `${n} ${s.replace(/_/g, " ")}`);
      return `There are ${repairs.length} repair jobs: ${parts.join(", ")}. Anything in "ready" is waiting on customer pickup.`;
    },
  },
  {
    id: "sales_analysis",
    match: ["sales", "revenue", "this month", "tracking", "how are sales", "trend"],
    run: ({ analytics, invoices }) => {
      const weekly = Array.isArray(analytics?.weekly_sales) ? analytics.weekly_sales : [];
      const weekTotal = weekly.reduce((s, d) => s + Number(d.total || 0), 0);
      const bills = invoices.length;
      return `Last 7 days booked about ${inr(weekTotal)} across the counter, with peak activity on ${
        analytics?.busiest_weekday || "weekends"
      } around ${analytics?.busiest_hour || "evening"}. ${bills} estimates/invoices are in the demo ledger.`;
    },
  },
  {
    id: "orders_summary",
    match: ["order", "orders", "custom", "work order", "production"],
    run: ({ orders }) => {
      const by = {};
      for (const o of orders) by[o.status] = (by[o.status] || 0) + 1;
      const parts = Object.entries(by).map(([s, n]) => `${n} ${s.replace(/_/g, " ")}`);
      return `You have ${orders.length} custom/work orders: ${parts.join(", ")}. The bridal temple necklace is the highest-value job in the pipeline.`;
    },
  },
  {
    id: "supplier_payable",
    match: ["supplier", "suppliers", "payable", "purchase due", "owe supplier", "vendor"],
    run: ({ suppliers = [], purchases = [] }) => {
      const owing = suppliers.filter((s) => Number(s.pending_cash || 0) > 0 || Number(s.pending_metal || 0) > 0);
      const cash = suppliers.reduce((s, x) => s + Number(x.pending_cash || 0), 0);
      const metal = suppliers.reduce((s, x) => s + Number(x.pending_metal || 0), 0);
      const top = [...owing].sort((a, b) => Number(b.pending_cash || 0) - Number(a.pending_cash || 0))[0];
      const pendingPurchases = purchases.filter((p) => p.status === "pending" || p.status === "rate_fixed").length;
      return `Across ${suppliers.length} suppliers you owe ${inr(cash)} plus ${metal.toFixed(
        2
      )} g fine, spread over ${pendingPurchases} open purchases.${
        top ? ` The largest payable is ${top.name} at ${inr(top.pending_cash)}.` : ""
      }`;
    },
  },
  {
    id: "price_suggestion",
    match: ["under", "budget", "less than", "below", "cheaper", "affordable", "suggest jewellery", "suggest jewelry"],
    run: ({ inventory = [] }, question) => {
      const m = String(question || "").replace(/[, ]/g, "").match(/(\d{4,})/);
      const cap = m ? Number(m[1]) : 50000;
      const priced = inventory
        .filter((i) => i.status === "available")
        .map((i) => ({
          name: i.item_name,
          est: Math.round(Number(i.net_weight || 0) * ((i.touch || 91.6) / 100) * 7320 + Number(i.making_charges || 0) + Number(i.stone_charges || 0)),
        }))
        .filter((i) => i.est > 0 && i.est <= cap)
        .sort((a, b) => b.est - a.est)
        .slice(0, 3);
      if (!priced.length) return `Nothing in stock falls under ${inr(cap)} right now — the lightest available pieces are studs and nose pins just above that.`;
      return `Under ${inr(cap)}, three good in-stock options: ${priced
        .map((p) => `${p.name} (~${inr(p.est)})`)
        .join(", ")}.`;
    },
  },
  {
    id: "follow_ups",
    match: ["follow up", "follow-up", "today", "to-do", "todo", "remind", "what should i", "priorit"],
    run: ({ repairs = [], orders = [], customers = [], suppliers = [] }) => {
      const readyRepairs = repairs.filter((r) => r.status === "ready").length;
      const dueOrders = orders.filter((o) => !["ready", "delivered", "completed"].includes(o.status)).length;
      const dueCustomers = customers.filter((c) => Number(c.pending_cash || 0) > 0).length;
      const dueSuppliers = suppliers.filter((s) => Number(s.pending_cash || 0) > 0).length;
      return `Today's shortlist: ${readyRepairs} repairs ready for pickup (call those customers first), ${dueOrders} custom orders in progress to chase, ${dueCustomers} customers carrying dues, and ${dueSuppliers} supplier payments pending. Start with ready repairs — that's cash already earned.`;
    },
  },
];

export function findRuleAnswer(question, data) {
  const q = String(question || "").toLowerCase();
  for (const rule of RULES) {
    if (rule.match.some((kw) => q.includes(kw))) {
      try {
        return rule.run(data, question);
      } catch {
        return null;
      }
    }
  }
  return null;
}

// Generic grounded fallback when nothing matches.
export function genericAnswer(data) {
  const { inventory = [], customers = [], repairs = [], orders = [] } = data;
  const available = inventory.filter((i) => i.status === "available").length;
  return `Here's a quick read on the demo shop: ${available} items in stock, ${customers.length} customers, ${repairs.length} active repairs and ${orders.length} custom orders. Ask about sales trend, slow-moving stock, customer dues, or repair status.`;
}
